import { History } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta, QueryDefinition } from '@reduxjs/toolkit/dist/query';
import { UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatagridWrapper, { DataGridId } from '../../components/DatagridWrapper';
import InfoButton from '../../components/InfoButton';
import InfoDrawer from '../../components/InfoDrawer';
import { OpenInNewTabButton } from '../../components/OpenInNewTabButton';
import { PaginationQueryPackage } from '../../models/API/QueryParams/PaginationQueryPackage';
import { ContactResponse } from '../../models/Contact';
import { HostingsiteResponse } from '../../models/Hostingsite';
import { OrganisationResponse } from '../../models/Organisation';
import { GetManyPackage } from '../../redux/GetManyPackage';
import { NotCorrectRights } from '../Permission/NotCorrectRights';
import { PermissionEnum } from '../Permission/PermissionEnum';
import { AbilityContext, Can } from '../UserApi/logic/Can';
import { HostingSiteInfoDialog } from './hostingSiteInfoDialog/HostingSiteInfoDialog';

class HostingSiteListProps {
    getHostingSites?: UseQuery<QueryDefinition<GetManyPackage, BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>, any, HostingsiteResponse[], any>>
    idToFetch?: string
    hostingSites?: HostingsiteResponse[]
    simpleView?: boolean = false;
    dataGridId?: DataGridId
}

export const HostingSiteList = (props: HostingSiteListProps) => {
    const ability = useContext(AbilityContext);

    const canReadList = ability.can(PermissionEnum.HOSTING_SITE_READ)
    if (!canReadList)
        return <NotCorrectRights />

    return (<HostingSiteListContent {...props} />)
}

const HostingSiteListContent = (props: HostingSiteListProps) => {
    const [clickedRow, setClickedRow] = useState<HostingsiteResponse | undefined>(undefined)
    const { t } = useTranslation()

    const contactGetter = (mapper: (contact: ContactResponse) => string | undefined) => {
        return (_: any, row: HostingsiteResponse) => row.contacts?.map(mapper).filter(value => value !== undefined && value !== "").join("; ")
    }

    const contactColumns: GridColDef[] = [
        { field: "contacts_name", sortable: true, type: "string", headerName: `${t('Contacts')} - ${t('Name')}`, valueGetter: contactGetter(contact => [contact.first_name, contact.last_name].filter(name => name).join(" ")), flex: 1 },
        { field: "contacts_email", sortable: true, type: "string", headerName: `${t('Contacts')} - ${t('Email')}`, valueGetter: contactGetter(contact => contact.email), flex: 1 },
        { field: "contacts_phone", sortable: true, type: "string", headerName: `${t('Contacts')} - ${t('Phone')}`, valueGetter: contactGetter(contact => contact.phone), flex: 1 },
    ]

    const getColumns = (hostingSites: HostingsiteResponse[]) => {

        const simpleView: GridColDef[] = [
            {
                headerName: t('Name'),
                field: "name",
                sortable: true,
                type: "string",
                flex: 1
            },
            {
                headerName: t('Address'),
                field: "address",
                sortable: true,
                type: "string",
                flex: 1
            },
            {
                headerName: t('Owner organisation'),
                field: 'owner_organisation',
                sortable: true,
                type: "string",
                valueGetter: (org: OrganisationResponse) => org?.name,
                flex: 2
            },
            {
                headerName: t('Administration organisation'),
                field: 'administrating_organisation',
                sortable: true,
                type: "string",
                valueGetter: (org: OrganisationResponse) => org?.name,
                flex: 2
            }
        ];
        const advancedView: GridColDef[] = [
            {
                headerName: t('SDN V4 IDs'),
                field: "sdn_v4_ids",
                sortable: true,
                type: "string",
                valueGetter: (_, row: HostingsiteResponse) => row.cpes?.map(cpe => cpe.sdn_v4_id).filter(sdn_v4_id => sdn_v4_id != undefined && sdn_v4_id != ""),
                flex: 1
            },
            {
                headerName: t('Theft marks'),
                field: "theft_marks",
                sortable: true,
                type: "string",
                valueGetter: (_, row: HostingsiteResponse) => row.cpes?.map(cpe => cpe.theft_mark).filter(theft_mark => theft_mark != undefined && theft_mark != ""),
                flex: 1
            },
            {
                headerName: t('Vlan names'),
                field: "vlan_names",
                sortable: true,
                type: "string",
                valueGetter: (_, row: HostingsiteResponse) => row.cpes?.flatMap(cpe => cpe.vlan_names),
                flex: 1
            }
        ];
        const buttonsView: GridColDef[] = [
            {
                headerName: t('Actions'),
                field: 'events',
                sortable: false,
                hideable: false,
                type: "string",
                valueGetter: () => "",
                minWidth: props.simpleView ? 100 : 220,
                renderCell: (params => {
                    const hostingSite = hostingSites.find(x => x.uuid === params.id)!;
                    return (props.simpleView ?
                        <OpenInNewTabButton url={`/hosting-sites/${hostingSite.uuid}`} />
                        :
                        <>
                            <InfoButton onClick={() => setClickedRow(hostingSite)} />
                            <Can I={PermissionEnum.AUDIT_READ} this={hostingSite}>
                                <OpenInNewTabButton tooltipText='Events' icon={<History />} url={`/events?entity_uuid=${hostingSite.uuid}`} />
                            </Can>
                            <OpenInNewTabButton url={`/hosting-sites/${hostingSite.uuid}`} />
                        </>
                    )
                })
            }
        ];

        const simpleList = simpleView;
        const advancedList = advancedView;
        const listToShow = props.simpleView ? simpleList : simpleList.concat(advancedList).concat(contactColumns);
        return listToShow.concat(buttonsView);
    }

    const hostingSites = getHostingSites(props)

    return (
        <>
            <DatagridWrapper
                dataGridId={props.dataGridId ?? DataGridId.HOSTING_SITES}
                onRowClick={(row) => { setClickedRow(row.row) }}
                loading={hostingSites == undefined}
                rows={hostingSites ?? []}
                columns={getColumns(hostingSites ?? [])}
                initialColumnVisibilityModel={Object.fromEntries(contactColumns.map(column => [column.field, false]))}
            />

            <InfoDrawer
                open={clickedRow != undefined}
                onClose={() => setClickedRow(undefined)}
                entityUuid={clickedRow?.uuid}>
                {clickedRow &&
                    <HostingSiteInfoDialog
                        key={clickedRow.uuid}
                        hostingSiteUuid={clickedRow.uuid}
                        afterDeleteAction={() => setClickedRow(undefined)} />}
            </InfoDrawer>
        </>
    )

    function getHostingSites(props: HostingSiteListProps) {
        if (props.hostingSites != undefined)
            return props.hostingSites


        const currentResult = props.getHostingSites?.({
            pagination: new PaginationQueryPackage(),
            uuid: props.idToFetch
        });

        return currentResult?.data
    }
}

